import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';

export function MfaSettingsPage() {
    const [mfaStatus, setMfaStatus] = useState(null);
    const [authenticators, setAuthenticators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSetupModal, setShowSetupModal] = useState(false);
    const [setupData, setSetupData] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [authenticatorType, setAuthenticatorType] = useState('google_authenticator');
    const [backupCodes, setBackupCodes] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ show: false, title: '', message: '', onConfirm: null });

    useEffect(() => {
        loadMfaData();
    }, []);

    function loadMfaData() {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.token) {
            return;
        }

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`
        };

        // Load MFA status
        fetch(`${config.apiUrl}/api/mfa/status`, { headers })
            .then(handleResponse)
            .then(status => {
                setMfaStatus(status);
            })
            .catch(error => {
                toast.error('Failed to load MFA status: ' + error);
            });

        // Load authenticators
        fetch(`${config.apiUrl}/api/mfa/authenticators`, { headers })
            .then(handleResponse)
            .then(auths => {
                setAuthenticators(auths);
                setLoading(false);
            })
            .catch(error => {
                toast.error('Failed to load authenticators: ' + error);
                setLoading(false);
            });
    }

    function handleResponse(response) {
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if (!response.ok) {
                const error = (data && data.message) || response.statusText;
                return Promise.reject(error);
            }
            return data;
        });
    }

    function startSetup() {
        if (!deviceName) {
            toast.error('Please enter a device name');
            return;
        }

        const user = JSON.parse(sessionStorage.getItem('user'));
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`
            },
            body: JSON.stringify({
                authenticatorType,
                deviceName
            })
        };

        fetch(`${config.apiUrl}/api/mfa/setup`, requestOptions)
            .then(handleResponse)
            .then(data => {
                setSetupData(data);
                setShowSetupModal(true);
            })
            .catch(error => {
                toast.error('Failed to initialize setup: ' + error);
            });
    }

    function verifySetup() {
        if (!verificationCode || verificationCode.length !== 6) {
            toast.error('Please enter a valid 6-digit code');
            return;
        }

        const user = JSON.parse(sessionStorage.getItem('user'));
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`
            },
            body: JSON.stringify({
                authenticatorId: setupData.authenticatorId,
                verificationCode
            })
        };

        fetch(`${config.apiUrl}/api/mfa/verify-setup`, requestOptions)
            .then(handleResponse)
            .then(result => {
                toast.success('Authenticator successfully activated!');
                setBackupCodes(result.backupCodes);
                setShowSetupModal(false);
                setVerificationCode('');
                setSetupData(null);
                setDeviceName('');
                loadMfaData();
            })
            .catch(error => {
                toast.error('Verification failed: ' + error);
            });
    }

    function removeAuthenticator(authenticatorId) {
        setConfirmDialog({
            show: true,
            title: 'Remove Authenticator',
            message: 'Are you sure you want to remove this authenticator? This action cannot be undone.',
            onConfirm: () => {
                setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });

                const user = JSON.parse(sessionStorage.getItem('user'));
                const requestOptions = {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                    }
                };

                fetch(`${config.apiUrl}/api/mfa/authenticators/${authenticatorId}`, requestOptions)
                    .then(handleResponse)
                    .then(result => {
                        toast.success(result.message);
                        if (result.mfaDisabled) {
                            toast.info('MFA has been disabled as this was your last authenticator');
                        }
                        loadMfaData();
                    })
                    .catch(error => {
                        toast.error('Failed to remove authenticator: ' + error);
                    });
            }
        });
    }

    function setDefaultAuthenticator(authenticatorId) {
        const user = JSON.parse(sessionStorage.getItem('user'));
        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`
            }
        };

        fetch(`${config.apiUrl}/api/mfa/authenticators/${authenticatorId}/default`, requestOptions)
            .then(handleResponse)
            .then(result => {
                toast.success(result.message);
                loadMfaData();
            })
            .catch(error => {
                toast.error('Failed to set default authenticator: ' + error);
            });
    }

    function regenerateBackupCodes() {
        setConfirmDialog({
            show: true,
            title: 'Regenerate Backup Codes',
            message: 'This will invalidate all existing backup codes. Any unused backup codes will no longer work. Continue?',
            onConfirm: () => {
                setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });

                const user = JSON.parse(sessionStorage.getItem('user'));
                const requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                    }
                };

                fetch(`${config.apiUrl}/api/mfa/backup-codes/regenerate`, requestOptions)
                    .then(handleResponse)
                    .then(result => {
                        setBackupCodes(result.backupCodes);
                        toast.success('Backup codes regenerated successfully');
                    })
                    .catch(error => {
                        toast.error('Failed to regenerate backup codes: ' + error);
                    });
            }
        });
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="row">
                <div className="col-lg-8 mx-auto">
                    <h2 className="fw-bold mb-4">Two-Factor Authentication</h2>

                    {/* MFA Status Card */}
                    <div className="modern-card mb-4">
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-2">
                                        <i className="bi bi-shield-lock me-2"></i>
                                        MFA Status
                                    </h5>
                                    <p className="text-muted mb-0">
                                        {mfaStatus?.mfaEnabled ? (
                                            <span className="badge bg-success">Enabled</span>
                                        ) : (
                                            <span className="badge bg-secondary">Disabled</span>
                                        )}
                                        {mfaStatus && (
                                            <span className="ms-3">
                                                {mfaStatus.activeAuthenticatorsCount} active authenticator(s)
                                            </span>
                                        )}
                                    </p>
                                </div>
                                {mfaStatus?.mfaEnabled && (
                                    <div>
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={regenerateBackupCodes}
                                        >
                                            Regenerate Backup Codes
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Add New Authenticator */}
                    <div className="modern-card mb-4">
                        <div className="card-body p-4">
                            <h5 className="mb-3">Add New Authenticator</h5>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Authenticator Type</label>
                                    <select
                                        className="form-select"
                                        value={authenticatorType}
                                        onChange={(e) => setAuthenticatorType(e.target.value)}
                                    >
                                        <option value="google_authenticator">Google Authenticator</option>
                                        <option value="microsoft_authenticator">Microsoft Authenticator</option>
                                        <option value="salesforce_authenticator">Salesforce Authenticator</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Device Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g., My iPhone"
                                        value={deviceName}
                                        onChange={(e) => setDeviceName(e.target.value)}
                                    />
                                </div>
                                <div className="col-12">
                                    <button
                                        className="btn btn-modern"
                                        onClick={startSetup}
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Authenticator
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Authenticators List */}
                    <div className="modern-card">
                        <div className="card-body p-4">
                            <h5 className="mb-3">Your Authenticators</h5>
                            {authenticators.length === 0 ? (
                                <p className="text-muted">No authenticators configured yet.</p>
                            ) : (
                                <div className="list-group list-group-flush">
                                    {authenticators.map(auth => (
                                        <div key={auth.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
                                            <div>
                                                <h6 className="mb-1">
                                                    <i className={`bi bi-${getAuthenticatorIcon(auth.authenticatorType)} me-2`}></i>
                                                    {auth.deviceName}
                                                    {auth.isDefault && (
                                                        <span className="badge bg-primary ms-2">Default</span>
                                                    )}
                                                    <span className={`badge ms-2 ${auth.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                                                        {auth.status}
                                                    </span>
                                                </h6>
                                                <small className="text-muted">
                                                    {formatAuthenticatorType(auth.authenticatorType)}
                                                    {auth.lastUsedAt && ` • Last used: ${new Date(auth.lastUsedAt).toLocaleString()}`}
                                                </small>
                                            </div>
                                            <div>
                                                {!auth.isDefault && auth.status === 'active' && (
                                                    <button
                                                        className="btn btn-sm btn-outline-primary me-2"
                                                        onClick={() => setDefaultAuthenticator(auth.id)}
                                                    >
                                                        Set as Default
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => removeAuthenticator(auth.id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Setup Modal */}
            {showSetupModal && setupData && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Set Up Authenticator</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowSetupModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body text-center">
                                <p>Scan this QR code with your authenticator app:</p>
                                {setupData.qrCodeDataUrl && (
                                    <img
                                        src={setupData.qrCodeDataUrl}
                                        alt="QR Code"
                                        className="img-fluid mb-3"
                                        style={{maxWidth: '250px'}}
                                    />
                                )}
                                <p className="small text-muted">
                                    Or enter this code manually: <br/>
                                    <code>{setupData.manualEntryKey}</code>
                                </p>
                                <div className="mt-4">
                                    <label className="form-label">Enter 6-Digit Verification Code</label>
                                    <input
                                        type="text"
                                        className="form-control text-center"
                                        placeholder="000000"
                                        maxLength="6"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                        style={{fontSize: '1.5rem', letterSpacing: '0.5rem'}}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowSetupModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-modern"
                                    onClick={verifySetup}
                                >
                                    Verify & Activate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Backup Codes Modal */}
            {backupCodes && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Backup Codes</h5>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Save these backup codes in a safe place. Each code can only be used once.
                                </div>
                                <div className="row">
                                    {backupCodes.map((code, index) => (
                                        <div key={index} className="col-6 mb-2">
                                            <code className="d-block p-2 bg-light rounded text-center">
                                                {code}
                                            </code>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-modern"
                                    onClick={() => setBackupCodes(null)}
                                >
                                    I've Saved My Codes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {confirmDialog.show && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                                    {confirmDialog.title}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">{confirmDialog.message}</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDialog.onConfirm}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function getAuthenticatorIcon(type) {
    switch (type) {
        case 'google_authenticator':
            return 'google';
        case 'microsoft_authenticator':
            return 'microsoft';
        case 'salesforce_authenticator':
            return 'cloud';
        case 'duo':
            return 'shield-check';
        default:
            return 'key';
    }
}

function formatAuthenticatorType(type) {
    switch (type) {
        case 'google_authenticator':
            return 'Google Authenticator';
        case 'microsoft_authenticator':
            return 'Microsoft Authenticator';
        case 'salesforce_authenticator':
            return 'Salesforce Authenticator';
        case 'duo':
            return 'DUO Security';
        default:
            return type;
    }
}
